import { Object3D, Quaternion, Vector3 } from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls';

const _tempQuaternion = new Quaternion();
const _alignVector = new Vector3(0, 1, 0);
const _tempQuaternion2 = new Quaternion();
const _identityQuaternion = new Quaternion();

const _unitX = new Vector3(1, 0, 0);
const _unitY = new Vector3(0, 1, 0);
const _unitZ = new Vector3(0, 0, 1);

class CustomTransformControls extends TransformControls {
  constructor(camera, domElement) {
    super(camera, domElement);
    // remove helpers
    Object.values(this._gizmo.helper).forEach((obj) => obj.removeFromParent());

    // patch updateMatrixWorld to update only used parts and apply proper material params
    this._gizmo.updateMatrixWorld = function (force) {
      const space = this.mode === 'scale' ? 'local' : this.space; // scale always oriented to local rotation

      const quaternion = space === 'local' ? this.worldQuaternion : _identityQuaternion;

      // Show only gizmos for current transform mode

      this.gizmo['translate'].visible = this.mode === 'translate';
      this.gizmo['rotate'].visible = this.mode === 'rotate';
      this.gizmo['scale'].visible = this.mode === 'scale';

      let handles = [];
      handles = handles.concat(this.picker[this.mode].children);
      handles = handles.concat(this.gizmo[this.mode].children);

      for (let i = 0; i < handles.length; i++) {
        const handle = handles[i];

        // hide aligned to camera

        handle.visible = true;
        handle.rotation.set(0, 0, 0);
        handle.position.copy(this.worldPosition);

        let factor;

        if (this.camera.isOrthographicCamera) {
          factor = (this.camera.top - this.camera.bottom) / this.camera.zoom;
        } else {
          factor =
            this.worldPosition.distanceTo(this.cameraPosition) *
            Math.min((1.9 * Math.tan((Math.PI * this.camera.fov) / 360)) / this.camera.zoom, 7);
        }

        handle.scale.set(1, 1, 1).multiplyScalar((factor * this.size) / 4);

        if (handle.tag === 'helper') {
          handle.visible = false;

          // If updating helper, skip rest of the loop
          continue;
        }

        // Align handles to current local or world rotation

        handle.quaternion.copy(quaternion);

        if (this.mode === 'translate' || this.mode === 'scale') {
          // Hide translate and scale axis facing the camera

          const AXIS_HIDE_THRESHOLD = 0.99;

          if (handle.name === 'X') {
            if (
              Math.abs(_alignVector.copy(_unitX).applyQuaternion(quaternion).dot(this.eye)) >
              AXIS_HIDE_THRESHOLD
            ) {
              handle.scale.set(1e-10, 1e-10, 1e-10);
              handle.visible = false;
            }
          }

          if (handle.name === 'Z') {
            if (
              Math.abs(_alignVector.copy(_unitZ).applyQuaternion(quaternion).dot(this.eye)) >
              AXIS_HIDE_THRESHOLD
            ) {
              handle.scale.set(1e-10, 1e-10, 1e-10);
              handle.visible = false;
            }
          }
        } else if (this.mode === 'rotate') {
          // Align handles to current local or world rotation

          _tempQuaternion2.copy(quaternion);
          _alignVector.copy(this.eye).applyQuaternion(_tempQuaternion.copy(quaternion).invert());

          if (handle.name === 'Y') {
            _tempQuaternion.setFromAxisAngle(_unitY, Math.atan2(_alignVector.x, _alignVector.z));
            _tempQuaternion.multiplyQuaternions(_tempQuaternion2, _tempQuaternion);
            handle.quaternion.copy(_tempQuaternion);
          }
        }

        // Hide disabled axes
        handle.visible = handle.visible && (handle.name.indexOf('X') === -1 || this.showX);
        handle.visible = handle.visible && (handle.name.indexOf('Y') === -1 || this.showY);
        handle.visible = handle.visible && (handle.name.indexOf('Z') === -1 || this.showZ);
        handle.visible =
          handle.visible && (handle.name.indexOf('E') === -1 || (this.showX && this.showY && this.showZ));

        // highlight selected axis

        handle.material._color = handle.material._color || handle.material.color.clone();
        handle.material._opacity = handle.material._opacity || handle.material.opacity;

        handle.material.color.copy(handle.material._color);
        handle.material.opacity = 0.5;

        if (this.enabled && this.axis) {
          if (handle.name === this.axis) {
            handle.material.opacity = 1.0;
          } else if (
            this.axis.split('').some(function (a) {
              return handle.name === a;
            })
          ) {
            handle.material.opacity = 1.0;
          }
        }
      }

      Object3D.prototype.updateMatrixWorld.call(this, force);
    };
  }
}

export class CombinedTransformControls extends Object3D {
  constructor(visualization) {
    super();
    const { camera, renderer } = visualization;
    this.translateControl = this.createTranslateControl(camera, renderer);
    this.rotateControl = this.createRotateControl(camera, renderer);
    this.add(this.translateControl, this.rotateControl);
  }
  setActiveControl(control) {
    if (control && this.activeControl) {
      return;
    }
    this.activeControl = control;
    this.translateControl.enabled = !control || this.translateControl === control;
    this.rotateControl.enabled = !control || this.rotateControl === control;
  }
  createTranslateControl(camera, renderer) {
    const control = new CustomTransformControls(camera, renderer.domElement);
    for (const child of [...control._gizmo.gizmo.translate.children]) {
      switch (child.name) {
        case 'X': {
          child.material.color.set(0xff3232);
          break;
        }
        case 'Z': {
          child.material.color.set(0x7dc5f8);
          break;
        }
        case 'XZ': {
          child.material.color.set(0x0f1c41);
          break;
        }
        default: {
          child.removeFromParent();
          child.visible = false;
        }
      }
    }
    control._gizmo.gizmo.translate.getObjectByName('X').geometry.scale(2, 1, 1).translate(-0.5, 0, 0);
    control._gizmo.gizmo.translate.getObjectByName('Z').geometry.scale(1, 1, 2).translate(0, 0, -0.5);
    control.setMode('translate');
    control.setSpace('local');
    control.setSize(0.7);
    control.showY = false;
    control.addEventListener('mouseDown', () => {
      this.setActiveControl(control);
    });
    control.addEventListener('mouseUp', () => {
      this.setActiveControl(null);
    });
    return control;
  }
  createRotateControl(camera, renderer) {
    const control = new CustomTransformControls(camera, renderer.domElement);
    for (const child of [...control._gizmo.gizmo.rotate.children]) {
      switch (child.name) {
        case 'Y': {
          child.material.color.set(0x0f1c41);
          break;
        }
        default: {
          child.removeFromParent();
          child.visible = false;
        }
      }
    }
    control.setMode('rotate');
    control.showX = false;
    control.showZ = false;
    control.addEventListener('mouseDown', () => {
      this.setActiveControl(control);
    });
    control.addEventListener('mouseUp', () => {
      this.setActiveControl(null);
    });
    return control;
  }
  addEventListener(type, listener) {
    this.translateControl.addEventListener(type, listener);
    this.rotateControl.addEventListener(type, listener);
  }
  detach() {
    this.object = null;
    this.translateControl.detach();
    this.rotateControl.detach();
  }
  attach(object) {
    this.object = object;
    this.translateControl.attach(object);
    this.rotateControl.attach(object);
  }
}
